import { AxiosInstance } from 'axios'
import qs from 'qs'

import { BaseAPI } from '../../classes/BaseAPI'
import { SearchRequestOptions } from '../../shared/types'
import { CreateEstablishmentDto, Establishment } from '../establishment/types'
import { CreateStaffDto, Staff } from '../staff/types'
import { Admin, CountByString, CountByWeek, CreateAdminDto, CreatePlugin, RankCount } from './types'

export class AdminAPI extends BaseAPI {
  constructor(axios: AxiosInstance) {
    super(axios)
  }

  createAdmin = async (createAdminDto: CreateAdminDto): Promise<Admin> => {
    return await this.request<Admin>('/admin', 'POST', createAdminDto)
  }

  updateSuperAdmin = async (emails: string[]): Promise<Admin> => {
    return await this.request<Admin>('/super-admin', 'PATCH', emails)
  }

  createEstablishment = async (establishmentDto: CreateEstablishmentDto): Promise<Establishment> => {
    return await this.request<Establishment>('/admin/etablissements', 'POST', establishmentDto)
  }

  deleteEstablishment = async (establishmentId: string): Promise<Establishment> => {
    return await this.request<Establishment>(`/admin/etablissements/${establishmentId}`, 'DELETE')
  }

  getEstablishments = async (): Promise<Establishment[]> => {
    return await this.request<Establishment[]>('/admin/etablissements', 'GET')
  }

  getEstablishment = async (): Promise<Establishment> => {
    return await this.request<Establishment>('/admin/etablissement', 'GET')
  }

  // returns staff from req.user.id
  getStaffs = async (filter: Record<string, unknown>, options?: SearchRequestOptions): Promise<[Staff[], number]> => {
    return await this.request<[Staff[], number]>(`/admin/staffs?${qs.stringify({ ...filter, ...options })}`, 'GET')
  }

  deleteStaff = async (staffId: string, associatedData?: string[]): Promise<Staff> => {
    const queryString = !associatedData || !associatedData.length ? '' : `?${qs.stringify({ associatedData })}`
    return await this.request<Staff>(`/admin/staffs/${staffId}${queryString}`, 'DELETE')
  }

  addStaff = async (staff: CreateStaffDto): Promise<Staff> => {
    return await this.request<Staff>(`/admin/staffs`, 'POST', staff)
  }

  addPlugin = async (plugin: CreatePlugin): Promise<Establishment> => {
    return await this.request<Establishment>(`/admin/plugins`, 'POST', plugin)
  }

  deletePlugin = async (plugin: CreatePlugin): Promise<CreatePlugin> => {
    return await this.request<CreatePlugin>(`/admin/plugins`, 'DELETE', plugin)
  }

  testPlugin = async (plugin: CreatePlugin): Promise<boolean> => {
    return await this.request<boolean>(`/plugin/ping?apiurl=${plugin.apiUrl}&apikey=${plugin.apiKey}&pluginurl=${plugin.pluginUrl}`, 'GET')
  }

  getConsultationsByWeek = async (etablissementId: string): Promise<CountByWeek[]> => {
    return await this.request<CountByWeek[]>(`/admin/etablissements/${etablissementId}/statistics/consultations`, 'GET')
  }

  getPatientsByWeek = async (etablissementId: string): Promise<CountByWeek[]> => {
    return await this.request<CountByWeek[]>(`/admin/etablissements/${etablissementId}/statistics/patients`, 'GET')
  }

  getConsultationsByStatus = async (etablissementId: string): Promise<CountByString[]> => {
    return await this.request<CountByString[]>(`/admin/etablissements/${etablissementId}/statistics/consultations/status`, 'GET')
  }

  getConsultationsByType = async (etablissementId: string): Promise<CountByString[]> => {
    return await this.request<CountByString[]>(`/admin/etablissements/${etablissementId}/statistics/consultations/type`, 'GET')
  }

  getConsultationsByStaff = async (etablissementId: string): Promise<RankCount[]> => {
    return await this.request<RankCount[]>(`/admin/etablissements/${etablissementId}/statistics/rank`, 'GET')
  }
}
